import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { GqlService, SiteGroupType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

import { SiteGroupsStore } from './site-groups.store';
import { SiteGroupModel } from '../site-group.model';

@Injectable({ providedIn: 'root' })
export class SiteGroupsService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private siteGroupsStore = inject(SiteGroupsStore);

  async add({
    description,
    name,
    rank_order,
  }: {
    description: null | string;
    name: string;
    rank_order: null | number;
  }) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createSiteGroup$({ description, name, rank_order })
    );
    let siteGroup: null | SiteGroupModel = null;

    if (success && data) {
      siteGroup = {
        ...data,
      } as SiteGroupModel;

      this.siteGroupsStore.add(siteGroup);
    }

    return { data: siteGroup, errors, success };
  }

  get(siteGroupTypes?: SiteGroupType[]) {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.siteGroupsStore.setLoading(true);
        this.siteGroupsStore.set([]);

        return this.gqlService
          .listSiteGroups$(siteGroupTypes || [SiteGroupType.SITE_GROUP_STANDARD])
          .pipe(
            map(({ data, errors, success }) => {
              let siteGroups = [] as SiteGroupModel[];

              if (data) {
                siteGroups = data.map((siteGroup) => {
                  return {
                    ...siteGroup,
                  } as SiteGroupModel;
                });
                this.siteGroupsStore.set(siteGroups);
              }

              this.siteGroupsStore.setLoading(false);

              return { data: siteGroups, errors, success };
            })
          );
      })
    );
  }

  async remove(siteGroup: SiteGroupModel) {
    const { errors, success } = await firstValueFrom(
      this.gqlService.removeSiteGroup$(siteGroup.id)
    );

    if (success) {
      this.siteGroupsStore.remove(siteGroup.id);
    }

    return { errors, success };
  }

  async update(siteGroup: SiteGroupModel) {
    this.siteGroupsStore.setLoading(true);

    const { data, errors, success } = await firstValueFrom(
      this.gqlService.updateSiteGroup$({
        description: siteGroup.description,
        id: siteGroup.id,
        name: siteGroup.name,
        rank_order: siteGroup.rank_order,
      })
    );

    if (success && data) {
      this.siteGroupsStore.update(siteGroup.id, siteGroup);
    }

    if (!success && errors) {
      this.overlayService.error(errors);
    }

    return { data, errors, success };
  }
}
